import type { Component } from 'vue'
import type { SiteBlock, SiteBlockDescriptor } from '../types/site'

/**
 * Maps a site block "type" key to everything needed to render and edit it.
 *
 * This is static app configuration — identical for every request and every
 * user — so a module-level Map is SSR-safe (it is not per-request state).
 * Each consuming app populates the registry once at app init via a plugin,
 * registering both the generic blocks this layer ships and its own
 * domain-specific blocks.
 *
 * The key is a plain string (not any single app's SiteBlockType union) so the
 * layer stays decoupled from each app's block vocabulary.
 */
const registry = new Map<string, SiteBlockDescriptor>()

/** Registration sequence, used to break ties when palette weights match. */
const sequence = new Map<string, number>()

const isDescriptor = (
  input: Component | SiteBlockDescriptor
): input is SiteBlockDescriptor =>
  typeof input === 'object' && input !== null && 'component' in input

/**
 * Registers a block type.
 *
 * Accepts either a full descriptor or — for backward compatibility with the
 * original renderer-only registry — a bare component, which is wrapped in a
 * minimal descriptor. A bare component still renders on public pages; it just
 * gets a placeholder palette entry and no settings form.
 */
export function registerSiteBlock(
  type: string,
  input: Component | SiteBlockDescriptor
) {
  const descriptor: SiteBlockDescriptor = isDescriptor(input)
    ? input
    : { component: input, meta: { label: type, icon: 'i-lucide-square' }, defaultData: {} }

  if (!sequence.has(type)) sequence.set(type, sequence.size)
  registry.set(type, descriptor)
}

export function useSiteBlockRegistry() {
  return {
    /** Renderer component for a type, or undefined if not registered. */
    get: (type: string) => registry.get(type)?.component,
    /** Full descriptor for a type — renderer, settings form, meta, defaults. */
    descriptor: (type: string) => registry.get(type),
    has: (type: string) => registry.has(type),
    /** Raw type → descriptor map. */
    all: () => registry,
    /**
     * Palette entries in curated order: explicit `meta.order` first (blocks
     * without one sort after), then registration order.
     */
    /**
     * Whether a block type may be dropped inside a container (columns): bleed
     * blocks own the full viewport width and containers can't nest.
     */
    canNest: (type: string) => {
      const d = registry.get(type)
      return !!d && !d.bleed && !d.container
    },
    isContainer: (type: string) => registry.get(type)?.container === true,
    palette: () =>
      [...registry.entries()]
        .map(([type, descriptor]) => ({ type, meta: descriptor.meta }))
        .sort((a, b) =>
          ((a.meta.order ?? Number.MAX_SAFE_INTEGER) - (b.meta.order ?? Number.MAX_SAFE_INTEGER))
          || ((sequence.get(a.type) ?? 0) - (sequence.get(b.type) ?? 0))
        )
  }
}

/**
 * Builds a fresh block of `type`, seeded from its registered `defaultData`.
 *
 * The settings seed mirrors the backend's default merge, so a block created
 * here and one normalised server-side agree.
 */
export const makeSiteBlockId = () =>
  globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`

export function makeSiteBlock(type: string): SiteBlock {
  const descriptor = registry.get(type)
  const defaults = descriptor?.defaultData ?? {}
  let data = structuredClone(toRaw(defaults))
  if (descriptor?.init) data = descriptor.init(data)

  return {
    id: makeSiteBlockId(),
    type,
    data,
    settings: { width: 'wide', text_size: 'm', background: 'none', spacing: 'm' }
  }
}
